/**
 * FinalizeTenderModal — soumet la version finale corrigée du tender.
 *
 * L'utilisateur peut :
 *   - Confirmer/modifier le SKU final de chaque ligne
 *   - Ajouter un commentaire par ligne (utilisé en few-shot pour l'apprentissage)
 *   - Ajouter une note globale sur le tender
 *
 * Une fois soumis :
 *   - Les corrections sont enregistrées dans `final_corrections`
 *   - Le tender passe en `reviewed`
 *   - L'agent ingère les corrections pour s'améliorer sur les futurs AO
 */

import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api-client';
import type { TenderLine } from '../lib/types';

type Props = {
  tenderId: string;
  lines: TenderLine[];
  onClose: () => void;
  onFinalized: () => void;
};

type LineEdit = {
  lineId: string;
  skuFinal: string;
  quantite: number;
  comment: string;
  changed: boolean;
};

export function FinalizeTenderModal({ tenderId, lines, onClose, onFinalized }: Props) {
  const [edits, setEdits] = useState<LineEdit[]>(() =>
    lines.map((l) => ({
      lineId: l.id,
      skuFinal: l.skuFinal ?? l.skuPropose ?? '',
      quantite: l.quantite ?? 0,
      comment: l.commentaireRevue ?? '',
      changed: false,
    })),
  );
  const [globalNote, setGlobalNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ correctionsRecorded: number } | null>(null);

  function updateEdit(lineId: string, patch: Partial<LineEdit>) {
    setEdits((arr) =>
      arr.map((e) => (e.lineId === lineId ? { ...e, ...patch, changed: true } : e)),
    );
  }

  const correctionsCount = edits.filter((e) => {
    if (!e.changed) return false;
    const orig = lines.find((l) => l.id === e.lineId);
    if (!orig) return false;
    return (
      e.skuFinal !== (orig.skuFinal ?? orig.skuPropose) ||
      e.quantite !== (orig.quantite ?? 0) ||
      (e.comment !== '' && e.comment !== (orig.commentaireRevue ?? ''))
    );
  }).length;

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.post<{ correctionsRecorded: number }>(
        `/api/tenders/${tenderId}/finalize`,
        {
          lines: edits
            .filter((e) => e.changed)
            .map((e) => ({
              lineId: e.lineId,
              skuFinal: e.skuFinal || null,
              quantite: e.quantite,
              comment: e.comment || null,
            })),
          globalNote: globalNote || null,
        },
      );
      setDone(r);
      setTimeout(() => {
        onFinalized();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 bg-ink-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <div className="tc-panel max-w-md w-full p-8 text-center">
          <div className="tc-label mb-2 text-emerald-400">success</div>
          <h2 className="text-xl font-display tracking-tight mb-3">Tender finalisé</h2>
          <p className="text-sm text-steel-300 mb-2 inline-flex items-center gap-2 justify-center">
            <CheckCircle2 aria-hidden className="w-4 h-4 text-emerald-400" strokeWidth={1.75} />
            <span>
              {done.correctionsRecorded} correction{done.correctionsRecorded > 1 ? 's' : ''} enregistrée{done.correctionsRecorded > 1 ? 's' : ''}
            </span>
          </p>
          <p className="text-xs text-steel-500">
            L'agent ingère ces corrections en arrière-plan pour s'améliorer sur les prochains AO.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 bg-ink-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="tc-panel max-w-4xl w-full max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <header className="px-6 py-4 border-b border-ink-700 flex items-center justify-between">
          <div>
            <div className="tc-label mb-1">tender / finalize</div>
            <h2 className="font-display text-lg tracking-tight">Soumettre la version finale</h2>
            <p className="text-xs text-steel-500 mt-1">
              Corrige le SKU final, la quantité ou ajoute un commentaire pour chaque ligne.
              L'agent apprend de tes corrections.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-steel-500 hover:text-steel-100 text-xl leading-none"
            aria-label="Fermer"
          >
            ×
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Note globale */}
          <div>
            <label className="tc-label block mb-1.5">global note · optional</label>
            <textarea
              value={globalNote}
              onChange={(e) => setGlobalNote(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder="Ex: Le client a confirmé que la pureté 95% est obligatoire pour ce projet"
              className="tc-input"
            />
          </div>

          {/* Lignes */}
          <div>
            <div className="tc-label mb-2">lines · {lines.length}</div>
            <div className="border border-ink-700">
              <table>
                <thead>
                  <tr>
                    <th>LINE</th>
                    <th>DESIGNATION</th>
                    <th className="w-32">SKU</th>
                    <th className="w-20 text-right">QTY</th>
                    <th className="w-64">COMMENT</th>
                  </tr>
                </thead>
                <tbody>
                  {edits.map((edit) => {
                    const orig = lines.find((l) => l.id === edit.lineId);
                    if (!orig) return null;
                    const skuChanged =
                      edit.skuFinal !== (orig.skuFinal ?? orig.skuPropose ?? '');
                    return (
                      <tr key={edit.lineId} className={skuChanged ? 'bg-amber-500/5' : ''}>
                        <td className="font-mono text-2xs text-steel-500">
                          {orig.numeroLigne ?? orig.id.slice(-6)}
                        </td>
                        <td className="text-xs max-w-md truncate">{orig.designationAo}</td>
                        <td>
                          <input
                            type="text"
                            value={edit.skuFinal}
                            onChange={(e) =>
                              updateEdit(edit.lineId, { skuFinal: e.target.value })
                            }
                            className={`w-full bg-ink-900 border px-2 py-1 text-xs font-mono ${
                              skuChanged
                                ? 'border-amber-500 text-amber-500'
                                : 'border-ink-700 text-steel-100'
                            }`}
                          />
                          {skuChanged && (
                            <div className="text-2xs text-steel-500 mt-0.5 font-mono">
                              was: {orig.skuPropose ?? '—'}
                            </div>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            value={edit.quantite}
                            onChange={(e) =>
                              updateEdit(edit.lineId, { quantite: Number(e.target.value) })
                            }
                            className="w-full bg-ink-900 border border-ink-700 px-2 py-1 text-xs font-mono text-right"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={edit.comment}
                            onChange={(e) =>
                              updateEdit(edit.lineId, { comment: e.target.value })
                            }
                            placeholder="raison de la correction..."
                            className="w-full bg-ink-900 border border-ink-700 px-2 py-1 text-xs"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="px-6 py-4 border-t border-ink-700 flex items-center justify-between">
          <div className="text-2xs font-mono text-steel-500">
            {correctionsCount > 0 ? (
              <>
                <span className="text-amber-500">{correctionsCount}</span> correction
                {correctionsCount > 1 ? 's' : ''} à soumettre
              </>
            ) : (
              'aucun changement'
            )}
          </div>
          {error && (
            <div className="text-2xs font-mono text-red-300 mr-4">ERROR · {error}</div>
          )}
          <div className="flex gap-2">
            <button onClick={onClose} className="tc-btn-secondary">
              cancel
            </button>
            <button onClick={submit} disabled={submitting} className="tc-btn-primary">
              {submitting ? 'submitting...' : `→ finalize${correctionsCount > 0 ? ` (${correctionsCount})` : ''}`}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
